import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import axios from 'axios';
import './AdminDashboard.css';
import AddTeacherHelp from './AddTeacherHelp';

// Abhaya Libre font for Sinhala hover translations
const ABHAYA_FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Abhaya+Libre:wght@400;500;600;700&display=swap');`;
if (typeof document !== 'undefined' && !document.getElementById('abhaya-font')) {
    const style = document.createElement('style');
    style.id = 'abhaya-font';
    style.innerHTML = ABHAYA_FONT_IMPORT;
    document.head.appendChild(style);
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

// WDMS Emp Report Tab Component
function WdmsReportTab({ report, loading, onRefresh, filter, setFilter }) {
    const filteredReport = report.filter(r => {
        if (!filter || !filter.trim()) return true;
        const q = filter.trim().toLowerCase();
        return (
            String(r.empId).includes(q) ||
            String(r.enrollmentId || '').includes(q) ||
            String(r.tid || '').includes(q) ||
            String(r.firstName || '').toLowerCase().includes(q) ||
            String(r.lastName || '').toLowerCase().includes(q) ||
            String(r.name || '').toLowerCase().includes(q) ||
            String(r.isActive || '').toLowerCase().includes(q) ||
            String(r.school || '').toLowerCase().includes(q)
        );
    });

    const headerStyle = {
        backgroundColor: '#1b5e20',
        color: '#ffffff',
        fontWeight: 'bold',
        padding: '10px 12px',
        textAlign: 'center',
        border: '0.5px solid #000000',
        position: 'sticky',
        top: '0',
        zIndex: '10'
    };

    const cellStyle = {
        padding: '8px 12px',
        border: '0.5px solid #000000'
    };

    const notInDbStyle = {
        ...cellStyle,
        backgroundColor: '#990000',
        color: '#ffffff'
    };

    const inactiveStyle = {
        ...cellStyle,
        backgroundColor: '#cc0000',
        color: '#ffffff'
    };

    const getRowStyle = (row) => {
        if (row.isActive === 'Not in DB') return notInDbStyle;
        if (row.isActive === 'Inactive') return inactiveStyle;
        return cellStyle;
    };

    return (
        <div style={{ marginTop: '0' }}>
            <div className="admin-section-header" style={{ marginBottom: '15px' }}>
                <h2><i className="pi pi-id-card"></i> WDMS Emp Report <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#64748b', marginLeft: '8px' }}>({report.length} emp_ids from WDMS)</span></h2>
                <div className="admin-toolbar">
                    <div className="admin-search-box">
                        <i className="pi pi-search"></i>
                        <InputText
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search emp_id, TID, name..."
                        />
                    </div>
                    <Button
                        label="Refresh"
                        icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                        className="p-button-outlined"
                        onClick={onRefresh}
                        disabled={loading}
                    />
                </div>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <ProgressSpinner />
                    <p style={{ marginTop: '10px', color: '#666' }}>Fetching WDMS data...</p>
                </div>
            )}

            {!loading && (
                <div style={{ maxHeight: '70vh', overflow: 'auto', border: '0.5px solid #000000' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>emp_id</th>
                                <th style={headerStyle}>enrollment_id</th>
                                <th style={headerStyle}>TID (Sign ID)</th>
                                <th style={headerStyle}>First Name</th>
                                <th style={headerStyle}>Last Name</th>
                                <th style={headerStyle}>Is Active</th>
                                <th style={headerStyle}>First Punch Date</th>
                                <th style={headerStyle}>Punch Count</th>
                                <th style={headerStyle}>School</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReport.map((row, i) => {
                                const rs = getRowStyle(row);
                                return (
                                    <tr key={row.empId}>
                                        <td style={rs}>{row.empId}</td>
                                        <td style={rs}>{row.enrollmentId || '-'}</td>
                                        <td style={rs}>{row.tid || '-'}</td>
                                        <td style={{ ...rs, textAlign: 'left' }}>{row.firstName || '-'}</td>
                                        <td style={{ ...rs, textAlign: 'left' }}>{row.lastName || '-'}</td>
                                        <td style={rs}>{row.isActive}</td>
                                        <td style={rs}>{row.firstPunch ? new Date(row.firstPunch).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                                        <td style={rs}>{row.punchCount}</td>
                                        <td style={{ ...rs, textAlign: 'left' }}>{row.school || '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && filteredReport.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    No records found.
                </div>
            )}

            {!loading && filteredReport.length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#666' }}>
                    Showing {filteredReport.length} of {report.length} records.
                    &nbsp;|&nbsp;
                    <span style={{ color: '#990000' }}>Dark red = Not in DB (punches dropped)</span>
                    &nbsp;|&nbsp;
                    <span style={{ color: '#cc0000' }}>Red = Inactive</span>
                </div>
            )}
        </div>
    );
}

function TeacherManagement() {
    const [activeTab, setActiveTab] = useState('teachers'); // 'teachers' | 'wdms-report'
    const [wdmsReport, setWdmsReport] = useState([]);
    const [wdmsReportLoading, setWdmsReportLoading] = useState(false);
    const [wdmsReportFilter, setWdmsReportFilter] = useState('');
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [teacherTypes, setTeacherTypes] = useState([]);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        nic: '',
        email: '',
        phone: '',
        teacherTypeId: null,
        enrollmentDate: null
    });
    const [statusFilter, setStatusFilter] = useState('active');
    const [nicLookupLoading, setNicLookupLoading] = useState(false);
    const [existingProfile, setExistingProfile] = useState(null);
    const [nicLookupStatus, setNicLookupStatus] = useState({ type: 'idle', message: '' });
    const [nextIds, setNextIds] = useState({ nextEmpId: null, nextTid: null });
    const [lastSavedTeacher, setLastSavedTeacher] = useState(null);
    const [empIdValidation, setEmpIdValidation] = useState({ valid: null, message: '' });
    const [tidConflict, setTidConflict] = useState(null);
    const [showConflictDialog, setShowConflictDialog] = useState(false);
    const [editingEmpId, setEditingEmpId] = useState({ teacherId: null, value: '', originalValue: '' });
    const [showPinDialog, setShowPinDialog] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const [empIdInlineValidation, setEmpIdInlineValidation] = useState({ teacherId: null, valid: null, message: '' });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [empIdHolderInfo, setEmpIdHolderInfo] = useState(null);
    const [backfillJobs, setBackfillJobs] = useState({}); // { teacherId: { jobId, status, total, processed } }
    const [draftTeacherId, setDraftTeacherId] = useState(null);
    const [draftBackfillPct, setDraftBackfillPct] = useState(0);
    const [draftBackfillStatus, setDraftBackfillStatus] = useState(null); // null | 'running' | 'completed' | 'failed'
    const [draftBackfillInfo, setDraftBackfillInfo] = useState({ total: 0, processed: 0 });

    const statusOptions = [
        { label: 'All Teachers', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
    ];
    const toast = React.useRef(null);
    const debounceTimer = useRef(null);

    const fetchWdmsReport = useCallback(async () => {
        setWdmsReportLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/wdms/emp-report`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWdmsReport(response.data.data || []);
        } catch (error) {
            console.error('Error fetching WDMS report:', error);
            showToast('error', 'Error', 'Failed to fetch WDMS report');
        } finally {
            setWdmsReportLoading(false);
        }
    }, []);

    const fetchTeachers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/teachers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeachers(response.data.data || []);
        } catch (error) {
            console.error('Error fetching teachers:', error);
            showToast('error', 'Error', 'Failed to fetch teachers');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchTeacherTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/teacher-types`);
            const types = response.data.filter(t => t.value !== 'All').map(t => ({
                label: t.label,
                value: t.value
            }));
            setTeacherTypes(types);
        } catch (error) {
            console.error('Error fetching teacher types:', error);
        }
    }, []);

    const validateEmpId = async (empId) => {
        if (!empId || empId.length < 1) {
            setEmpIdValidation({ valid: null, message: '' });
            return;
        }

        // Track latest request to avoid race conditions
        validateEmpId.latestEmpId = empId;

        // Set loading state
        setEmpIdValidation({ valid: null, message: 'Validating...' });

        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/wdms/validate-emp-id/${encodeURIComponent(empId)}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000
            });
            
            // Ignore stale responses from earlier requests
            if (validateEmpId.latestEmpId !== empId) return;
            
            if (response.data.valid) {
                setEmpIdValidation({ valid: true, message: response.data.message });

                // Autofill form ONLY if the user hasn't changed the emp_id field
                // (prevents stale autofill when user types past a partial match)
                setFormData(prev => {
                    // If user has typed more/different text, don't autofill
                    if (prev.empId !== empId) return prev;

                    const profile = response.data.profile;
                    if (profile && profile.profileId) {
                        showToast('info', 'Profile Found', `Existing profile for emp_id ${empId} loaded — review and save.`);
                        return {
                            ...prev,
                            firstName: profile.firstName || prev.firstName,
                            lastName: profile.lastName || prev.lastName,
                            nic: profile.nic || prev.nic,
                            email: profile.email || prev.email,
                            phone: profile.phone || prev.phone,
                            teacherTypeId: profile.teacherTypeId || prev.teacherTypeId,
                            tid: profile.tid || prev.tid,
                            existingProfileId: profile.profileId
                        };
                    }
                    return prev;
                });
            } else {
                setEmpIdValidation({ valid: false, message: response.data.message });
            }
        } catch (error) {
            // Ignore stale responses from earlier requests
            if (validateEmpId.latestEmpId !== empId) return;
            
            console.error('Error validating emp_id:', error);
            const msg = error.response?.status === 401
                ? 'Session expired — please reload'
                : 'Unable to validate emp_id against WDMS';
            setEmpIdValidation({ valid: null, message: msg });
        }
    };

    const validateNicFormat = (nic) => {
        if (!nic || nic.length < 5) return null; // too short, don't validate yet
        const clean = nic.trim().toUpperCase();
        const oldNicPattern = /^\d{9}V$/;
        const newNicPattern = /^\d{12}$/;
        if (oldNicPattern.test(clean) || newNicPattern.test(clean)) {
            return true;
        }
        return false;
    };

    const lookupNic = async (nic) => {
        if (!nic || nic.length < 5) {
            setExistingProfile(null);
            setNicLookupStatus({ type: 'idle', message: '' });
            return;
        }

        // Validate NIC format first
        const formatValid = validateNicFormat(nic);
        if (formatValid === false) {
            setExistingProfile(null);
            setNicLookupStatus({ type: 'error', message: 'Invalid NIC format (e.g., 883382951V or 198955200637)' });
            return;
        }

        const normalizedNic = String(nic).trim().replace(/\s+/g, '').toUpperCase();
        
        setNicLookupLoading(true);
        setNicLookupStatus({ type: 'loading', message: 'Checking NIC… If it exists, details will load automatically.' });
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/teachers/lookup-nic/${encodeURIComponent(normalizedNic)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.exists) {
                const profile = response.data.data;
                console.log('[NIC Lookup] API Response:', profile);
                console.log('[NIC Lookup] TID from API:', profile.tid);
                console.log('[NIC Lookup] Previous TID:', prev => prev.tid);
                setExistingProfile(profile);
                setNicLookupStatus({ type: 'exists', message: 'This NIC already exists. Details have been loaded — you can edit them before saving.' });
                // Auto-fill form with existing data
                setFormData(prev => {
                    const newTid = profile.tid !== null && profile.tid !== undefined ? profile.tid : prev.tid;
                    console.log('[NIC Lookup] Setting TID to:', newTid);
                    return {
                        ...prev,
                        firstName: (profile.first_name || '').trim(),
                        lastName: (profile.last_name || '').trim(),
                        nic: (profile.nic || '').trim().toUpperCase(),
                        email: (profile.email || '').trim().toLowerCase(),
                        phone: (profile.mobile_phone || '').trim(),
                        teacherTypeId: profile.teacherType || null,
                        existingProfileId: profile.profileId,
                        empId: profile.empId || prev.empId,
                        tid: newTid,
                        enrollmentDate: profile.enrollmentDate || null
                    };
                });
            } else {
                setExistingProfile(null);
                setNicLookupStatus({ type: 'not_found', message: 'New teacher enrollment — Enter details below to create profile and teacher record.' });

                // Auto-create draft + start backfill if emp_id is validated and TID is present
                if (formData.empId && empIdValidation.valid && formData.tid && !draftTeacherId) {
                    try {
                        const token = localStorage.getItem('adminToken');
                        const draftRes = await axios.post(`${API_BASE_URL}/admin/teachers/draft`, {
                            empId: formData.empId,
                            tid: formData.tid,
                            nic: normalizedNic
                        }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });

                        if (draftRes.data.success) {
                            setDraftTeacherId(draftRes.data.teacherId);
                            setDraftBackfillStatus('running');
                            setDraftBackfillInfo({ total: 0, processed: 0 });
                            setDraftBackfillPct(0);

                            // Start polling backfill progress
                            const jobId = draftRes.data.backfillJobId;
                            const pollInterval = setInterval(async () => {
                                try {
                                    const statusRes = await axios.get(`${API_BASE_URL}/admin/teachers/backfill/${jobId}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                    const job = statusRes.data;
                                    const total = job.total || 0;
                                    const processed = job.processed || 0;
                                    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
                                    setDraftBackfillInfo({ total, processed });
                                    setDraftBackfillPct(pct);

                                    if (job.status === 'completed') {
                                        setDraftBackfillStatus('completed');
                                        clearInterval(pollInterval);
                                    } else if (job.status === 'failed') {
                                        setDraftBackfillStatus('failed');
                                        clearInterval(pollInterval);
                                    }
                                } catch (e) {
                                    console.error('Draft backfill poll error:', e);
                                }
                            }, 2000);
                        }
                    } catch (draftErr) {
                        console.error('Draft creation error:', draftErr);
                        // Don't block — admin can still save manually
                    }
                }
            }
        } catch (error) {
            console.error('Error looking up NIC:', error);
            setExistingProfile(null);
            setNicLookupStatus({ type: 'error', message: 'Could not check this NIC right now. Please try again.' });
        } finally {
            setNicLookupLoading(false);
        }
    };

    useEffect(() => {
        fetchTeachers();
        fetchTeacherTypes();
    }, [fetchTeachers, fetchTeacherTypes]);

    useEffect(() => {
        if (activeTab === 'wdms-report' && wdmsReport.length === 0 && !wdmsReportLoading) {
            fetchWdmsReport();
        }
    }, [activeTab]);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    // Poll backfill job statuses
    useEffect(() => {
        const runningJobs = Object.entries(backfillJobs).filter(([_, job]) => job.status === 'running');
        if (runningJobs.length === 0) return;

        const token = localStorage.getItem('adminToken');
        const interval = setInterval(async () => {
            const updates = {};
            for (const [teacherId, job] of Object.entries(backfillJobs)) {
                if (job.status !== 'running') continue;
                try {
                    const res = await axios.get(`${API_BASE_URL}/admin/teachers/backfill/${job.jobId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    updates[teacherId] = res.data;

                    if (res.data.status === 'completed') {
                        const teacher = teachers.find(t => String(t.id) === teacherId);
                        showToast('success', 'Backfill Complete', `${teacher?.firstName || 'Teacher'}: ${res.data.message}`);
                        fetchTeachers();
                    } else if (res.data.status === 'failed') {
                        const teacher = teachers.find(t => String(t.id) === teacherId);
                        showToast('error', 'Backfill Failed', `${teacher?.firstName || 'Teacher'}: ${res.data.message}`);
                    }
                } catch (error) {
                    console.error('Error polling backfill:', error);
                }
            }

            if (Object.keys(updates).length > 0) {
                setBackfillJobs(prev => ({ ...prev, ...updates }));
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [backfillJobs, teachers]);

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    const fetchNextIds = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/teachers/next-ids`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNextIds({ nextEmpId: response.data.nextEmpId, nextTid: response.data.nextTid });
            return response.data;
        } catch (error) {
            console.error('Error fetching next IDs:', error);
            return { nextEmpId: null, nextTid: null };
        }
    };

    const openNewDialog = async () => {
        const ids = await fetchNextIds();
        setFormData({
            firstName: '',
            lastName: '',
            nic: '',
            email: '',
            phone: '',
            teacherTypeId: null,
            existingProfileId: null,
            empId: ids.nextEmpId || '',
            tid: ids.nextTid || '',
            enrollmentDate: null
        });
        setEditMode(false);
        setSelectedTeacher(null);
        setExistingProfile(null);
        setNicLookupStatus({ type: 'idle', message: '' });
        setLastSavedTeacher(null);
        setDraftTeacherId(null);
        setDraftBackfillStatus(null);
        setDraftBackfillPct(0);
        setDraftBackfillInfo({ total: 0, processed: 0 });
        setShowDialog(true);
    };

    const openEditDialog = (teacher) => {
        setFormData({
            firstName: teacher.firstName || '',
            lastName: teacher.lastName || '',
            nic: teacher.nic || '',
            email: teacher.email || '',
            phone: teacher.phone || '',
            teacherTypeId: teacher.teacherType || null,
            enrollmentDate: teacher.enrollmentDate || null
        });
        setEditMode(true);
        setSelectedTeacher(teacher);
        setShowDialog(true);
    };

    const handleSave = async () => {
        // Validate all required fields
        const missing = [];
        if (!formData.nic || !formData.nic.trim()) missing.push('NIC');
        if (!formData.firstName || !formData.firstName.trim()) missing.push('First Name');
        if (!formData.lastName || !formData.lastName.trim()) missing.push('Last Name');
        if (!formData.teacherTypeId) missing.push('Teacher Type');
        if (!formData.email || !formData.email.trim()) missing.push('Email');
        if (!formData.phone || !formData.phone.trim()) missing.push('WhatsApp No');
        if (!editMode && (!formData.empId || !String(formData.empId).trim())) missing.push('Enrolment ID (emp_ID)');
        if (missing.length > 0) {
            showToast('warn', 'Validation', `Please fill required fields: ${missing.join(', ')}`);
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            if (editMode && selectedTeacher) {
                await axios.put(`${API_BASE_URL}/admin/teachers/${selectedTeacher.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showToast('success', 'Success', 'Teacher updated successfully');
                setShowDialog(false);
            } else {
                const response = await axios.post(`${API_BASE_URL}/admin/teachers`, {
                    ...formData,
                    existingProfileId: formData.existingProfileId || null,
                    draftTeacherId: draftTeacherId || null
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showToast('success', 'Success', 'Teacher added successfully');
                setLastSavedTeacher({
                    ...formData,
                    empId: response.data.empId || formData.empId,
                    tid: response.data.tid || formData.tid
                });
            }
            fetchTeachers();
        } catch (error) {
            console.error('Error saving teacher:', error);
            if (error.response?.status === 409 && error.response?.data?.conflict) {
                // TID conflict - show conflict dialog
                setTidConflict(error.response.data.conflict);
                setShowConflictDialog(true);
            } else {
                showToast('error', 'Error', error.response?.data?.message || 'Failed to save teacher');
            }
        }
    };

    const handleReassignTid = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${API_BASE_URL}/admin/teachers/reassign-tid`, {
                conflictTeacherId: tidConflict.teacherId,
                newTeacherId: existingProfile?.teacherId || null,
                tid: tidConflict.tid
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            showToast('success', 'Success', `TID ${tidConflict.tid} reassigned successfully`);
            setShowConflictDialog(false);
            setTidConflict(null);
            
            // Retry the save
            handleSave();
        } catch (error) {
            console.error('Error reassigning TID:', error);
            showToast('error', 'Error', error.response?.data?.message || 'Failed to reassign TID');
        }
    };

    const printRegistration = () => {
        const t = lastSavedTeacher || formData;
        const typeName = teacherTypes.find(tt => tt.value === t.teacherTypeId)?.label || t.teacherTypeId || '-';
        const printWindow = window.open('', '_blank', 'width=600,height=500');
        printWindow.document.write(`
            <html><head><title>Teacher Registration</title>
            <style>
                body { font-family: Arial, sans-serif; padding: 30px; }
                h2 { color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px; }
                table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                td { padding: 8px 12px; border: 1px solid #ddd; }
                td:first-child { font-weight: bold; width: 40%; background: #f5f5f5; }
                .header { text-align: center; margin-bottom: 10px; }
                .note { margin-top: 20px; padding: 10px; background: #fff3cd; border-radius: 5px; font-size: 0.9rem; }
                .ids { display: flex; gap: 20px; margin: 15px 0; }
                .id-box { flex: 1; text-align: center; padding: 15px; border-radius: 8px; }
                .id-box.emp { background: #e3f2fd; border: 2px solid #1976d2; }
                .id-box.sign { background: #e8f5e9; border: 2px solid #388e3c; }
                .id-box .label { font-size: 0.8rem; color: #666; }
                .id-box .value { font-size: 1.8rem; font-weight: bold; }
            </style></head><body>
            <div class="header">
                <h2>ANANDA COLLEGE - New Teacher Registration</h2>
                <p>Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div class="ids">
                <div class="id-box emp"><div class="label">Enrolment ID (emp_ID)</div><div class="value">${t.empId}</div></div>
                <div class="id-box sign"><div class="label">Sign ID (TID)</div><div class="value">${t.tid}</div></div>
            </div>
            <table>
                <tr><td>NIC</td><td>${t.nic}</td></tr>
                <tr><td>First Name</td><td>${t.firstName}</td></tr>
                <tr><td>Last Name</td><td>${t.lastName}</td></tr>
                <tr><td>Teacher Type</td><td>${typeName}</td></tr>
                <tr><td>Email</td><td>${t.email}</td></tr>
                <tr><td>WhatsApp No</td><td>${t.phone}</td></tr>
            </table>
            <div class="note">
                <strong>Note:</strong> Please present this form at the office to complete biometric registration on the attendance machine using <strong>Enrolment ID: ${t.empId}</strong>.
            </div>
            </body></html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const handleToggleStatus = async (teacher) => {
        const newStatus = teacher.isActive ? 0 : 1;
        const action = newStatus ? 'activate' : 'deactivate';
        
        confirmDialog({
            message: `Are you sure you want to ${action} ${teacher.firstName} ${teacher.lastName}?`,
            header: 'Confirm Action',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: newStatus ? 'p-button-success' : 'p-button-danger',
            accept: async () => {
                try {
                    const token = localStorage.getItem('adminToken');
                    await axios.put(`${API_BASE_URL}/admin/teachers/${teacher.id}/status`, 
                        { isActive: newStatus },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    showToast('success', 'Success', `Teacher ${action}d successfully`);
                    fetchTeachers();
                } catch (error) {
                    console.error('Error updating status:', error);
                    showToast('error', 'Error', 'Failed to update teacher status');
                }
            }
        });
    };

    const handleDelete = (teacher) => {
        confirmDialog({
            message: `Are you sure you want to delete ${teacher.firstName} ${teacher.lastName}? This action cannot be undone.`,
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const token = localStorage.getItem('adminToken');
                    await axios.delete(`${API_BASE_URL}/admin/teachers/${teacher.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    showToast('success', 'Success', 'Teacher deleted successfully');
                    fetchTeachers();
                } catch (error) {
                    console.error('Error deleting teacher:', error);
                    showToast('error', 'Error', error.response?.data?.message || 'Failed to delete teacher');
                }
            }
        });
    };

    const statusTemplate = (rowData) => {
        return (
            <Button
                label={rowData.isActive ? 'Active' : 'Inactive'}
                icon={rowData.isActive ? 'pi pi-check' : 'pi pi-times'}
                className={`p-button-sm ${rowData.isActive ? 'p-button-success' : 'p-button-danger'}`}
                onClick={() => handleToggleStatus(rowData)}
                style={{ minWidth: '92px' }}
            />
        );
    };

    const handleBackfill = async (teacher) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.post(
                `${API_BASE_URL}/admin/teachers/${teacher.id}/backfill`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const { jobId } = response.data;
            setBackfillJobs(prev => ({
                ...prev,
                [teacher.id]: { jobId, status: 'running', total: 0, processed: 0 }
            }));
            showToast('info', 'Backfill Started', `Backfill started for ${teacher.firstName} ${teacher.lastName}`);
        } catch (error) {
            console.error('Error starting backfill:', error);
            showToast('error', 'Backfill Failed', error.response?.data?.message || 'Could not start backfill');
        }
    };

    const actionsTemplate = (rowData) => {
        const bf = backfillJobs[rowData.id];
        const isBackfilling = bf && bf.status === 'running';
        const pct = isBackfilling && bf.total > 0 ? Math.round((bf.processed / bf.total) * 100) : 0;
        return (
            <div className="action-buttons" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                        icon={isBackfilling ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                        className={`p-button-rounded p-button-sm ${isBackfilling ? 'p-button-warning' : 'p-button-help'}`}
                        tooltip="Backfill"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleBackfill(rowData)}
                        disabled={isBackfilling}
                    />
                    <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-info p-button-sm"
                        tooltip="Edit"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => openEditDialog(rowData)}
                    />
                    <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-sm"
                        tooltip="Delete"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleDelete(rowData)}
                    />
                </div>
                {isBackfilling && (
                    <div style={{ width: '100%', marginTop: '2px' }}>
                        <div style={{
                            width: '100%', height: '18px', backgroundColor: '#e5e7eb',
                            borderRadius: '9px', overflow: 'hidden', position: 'relative'
                        }}>
                            <div style={{
                                width: `${pct}%`, height: '100%',
                                background: 'linear-gradient(90deg, #f59e0b, #f97316)',
                                borderRadius: '9px', transition: 'width 0.5s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'white' }}>
                                    {bf.total > 0 ? `${bf.processed}/${bf.total}` : '...'}
                                </span>
                            </div>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'center', marginTop: '1px' }}>
                            {bf.total > 0 ? `${pct}%` : 'Fetching from WDMS...'}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const statusActionsTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <div>{statusTemplate(rowData)}</div>
                {actionsTemplate(rowData)}
            </div>
        );
    };

    const indexTemplate = (rowData, options) => {
        return options.rowIndex + 1;
    };

    const lastPunchDateTemplate = (rowData) => {
        if (!rowData.lastPunchDate) return '-';
        const d = new Date(rowData.lastPunchDate);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const empIdEditor = (options) => {
        const validation = empIdInlineValidation;
        const isLoading = validation.teacherId === options.rowData.id && validation.valid === null && validation.message !== '';
        const isValid = validation.teacherId === options.rowData.id && validation.valid === true;
        const isInvalid = validation.teacherId === options.rowData.id && validation.valid === false;
        
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <InputText
                    value={options.value}
                    onChange={(e) => {
                        options.editorCallback(e.target.value);
                        setEditingEmpId({ teacherId: options.rowData.id, value: e.target.value });
                        // Validate against WDMS with debouncing
                        clearTimeout(debounceTimer.current);
                        debounceTimer.current = setTimeout(() => {
                            validateEmpIdInline(options.rowData.id, e.target.value);
                        }, 500);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.target.blur();
                        }
                    }}
                    style={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 'bold', 
                        textAlign: 'center',
                        borderColor: isValid ? '#2e7d32' : isInvalid ? '#c62828' : undefined
                    }}
                />
                {isLoading && <i className="pi pi-spin pi-spinner" style={{ color: '#666', fontSize: '0.9rem' }} />}
                {isValid && <i className="pi pi-check" style={{ color: '#2e7d32', fontSize: '0.9rem' }} />}
                {isInvalid && <i className="pi pi-times" style={{ color: '#c62828', fontSize: '0.9rem' }} />}
            </div>
        );
    };

    const validateEmpIdInline = async (teacherId, empId) => {
        if (!empId || empId.length < 1) {
            setEmpIdInlineValidation({ teacherId, valid: null, message: '' });
            return;
        }

        // Track latest request to avoid race conditions
        validateEmpIdInline.latestKey = `${teacherId}:${empId}`;

        // Set loading state
        setEmpIdInlineValidation({ teacherId, valid: null, message: 'Validating...' });

        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/wdms/validate-emp-id/${encodeURIComponent(empId)}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000
            });
            
            // Ignore stale responses from earlier requests
            if (validateEmpIdInline.latestKey !== `${teacherId}:${empId}`) return;
            
            if (response.data.valid) {
                setEmpIdInlineValidation({ teacherId, valid: true, message: response.data.message });
            } else {
                setEmpIdInlineValidation({ teacherId, valid: false, message: response.data.message });
            }
        } catch (error) {
            // Ignore stale responses from earlier requests
            if (validateEmpIdInline.latestKey !== `${teacherId}:${empId}`) return;
            
            console.error('Error validating emp_id:', error);
            const msg = error.response?.status === 401
                ? 'Session expired — please reload'
                : 'Unable to validate emp_id against WDMS';
            setEmpIdInlineValidation({ teacherId, valid: null, message: msg });
        }
    };

    const empIdBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{rowData.empId || '-'}</span>
                <i className="pi pi-pencil" style={{ fontSize: '0.8rem', color: '#64748b', cursor: 'pointer' }}></i>
            </div>
        );
    };

    const onEmpIdEditComplete = async (e) => {
        const { rowData, newValue, value } = e;
        
        // If value didn't change, do nothing
        if (newValue === value) {
            return;
        }

        // Check WDMS validation
        const validation = empIdInlineValidation;
        if (validation.teacherId === rowData.id && validation.valid === false) {
            showToast('error', 'Validation Error', validation.message);
            // Revert the change by returning the original value
            e.value = value;
            return;
        }

        // Fetch emp_id holder information
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/teachers/emp-id/${newValue}/holder`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setEditingEmpId({ teacherId: rowData.id, value: newValue, originalValue: value, teacherName: `${rowData.firstName} ${rowData.lastName}` });
            setEmpIdHolderInfo(response.data);
            setShowConfirmDialog(true);
            // Revert the change temporarily until confirmation
            e.value = value;
        } catch (error) {
            console.error('Error fetching emp_id holder:', error);
            showToast('error', 'Error', 'Failed to check emp_id holder information');
            e.value = value;
        }
    };

    const handleConfirmYes = () => {
        setShowConfirmDialog(false);
        setShowPinDialog(true);
    };

    const handleConfirmNo = () => {
        setShowConfirmDialog(false);
        setEmpIdHolderInfo(null);
        setEditingEmpId({ teacherId: null, value: '', originalValue: '' });
    };

    const handlePinSubmit = async () => {
        if (pinInput !== '0218') {
            showToast('error', 'Invalid PIN', 'Incorrect PIN. emp_id change cancelled.');
            setShowPinDialog(false);
            setPinInput('');
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.put(
                `${API_BASE_URL}/admin/teachers/${editingEmpId.teacherId}/emp-id`,
                { empId: editingEmpId.value, pin: pinInput },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                showToast('success', 'Success', 'emp_id updated successfully');
                fetchTeachers();
            } else {
                showToast('error', 'Error', response.data.message);
            }
        } catch (error) {
            console.error('Error updating emp_id:', error);
            if (error.response?.data?.message) {
                showToast('error', 'Error', error.response.data.message);
            } else {
                showToast('error', 'Error', 'Failed to update emp_id');
            }
        } finally {
            setShowPinDialog(false);
            setPinInput('');
            setEditingEmpId({ teacherId: null, value: '' });
        }
    };

    const header = (
        <div className="admin-section-header">
            <h2><i className="pi pi-users"></i> Teacher Management <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#64748b', marginLeft: '8px' }}>({teachers.filter(t => {
                    if (statusFilter === 'active') return t.isActive === 1 || t.isActive === true;
                    if (statusFilter === 'inactive') return t.isActive === 0 || t.isActive === false;
                    return true;
                }).length} teachers)</span></h2>
            <div className="admin-toolbar">
                <Dropdown
                    value={statusFilter}
                    options={statusOptions}
                    onChange={(e) => setStatusFilter(e.value)}
                    placeholder="Filter by Status"
                    style={{ minWidth: '150px', marginRight: '10px' }}
                />
                <div className="admin-search-box">
                    <i className="pi pi-search"></i>
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Enter Sign ID (TID) or NIC"
                    />
                </div>
                <Button
                    label="Add Teacher"
                    icon="pi pi-plus"
                    className="p-button-success"
                    onClick={openNewDialog}
                />
                <Button
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    tooltip="Refresh"
                    onClick={fetchTeachers}
                />
            </div>
        </div>
    );

    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label="Save" icon="pi pi-check" onClick={handleSave} disabled={!!lastSavedTeacher && !editMode} />
        </div>
    );

    return (
        <div className="admin-section">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Sub-tab navigation */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '0', borderBottom: '2px solid #1b5e20' }}>
                <button
                    onClick={() => setActiveTab('teachers')}
                    style={{
                        padding: '10px 24px',
                        border: 'none',
                        background: activeTab === 'teachers' ? '#1b5e20' : 'transparent',
                        color: activeTab === 'teachers' ? '#fff' : '#1b5e20',
                        fontWeight: activeTab === 'teachers' ? 'bold' : 'normal',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        borderRadius: '4px 4px 0 0',
                        borderBottom: activeTab === 'teachers' ? '2px solid #1b5e20' : '2px solid transparent',
                        marginBottom: '-2px'
                    }}
                >
                    <i className="pi pi-users" style={{ marginRight: '8px' }}></i>
                    Teacher List
                </button>
                <button
                    onClick={() => setActiveTab('wdms-report')}
                    style={{
                        padding: '10px 24px',
                        border: 'none',
                        background: activeTab === 'wdms-report' ? '#1b5e20' : 'transparent',
                        color: activeTab === 'wdms-report' ? '#fff' : '#1b5e20',
                        fontWeight: activeTab === 'wdms-report' ? 'bold' : 'normal',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        borderRadius: '4px 4px 0 0',
                        borderBottom: activeTab === 'wdms-report' ? '2px solid #1b5e20' : '2px solid transparent',
                        marginBottom: '-2px'
                    }}
                >
                    <i className="pi pi-id-card" style={{ marginRight: '8px' }}></i>
                    WDMS Emp Report
                </button>
            </div>

            {activeTab === 'wdms-report' && (
                <WdmsReportTab
                    report={wdmsReport}
                    loading={wdmsReportLoading}
                    onRefresh={fetchWdmsReport}
                    filter={wdmsReportFilter}
                    setFilter={setWdmsReportFilter}
                />
            )}

            {activeTab === 'teachers' && (
            <>
            
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            <DataTable
                value={teachers.filter(t => {
                    // Status filter
                    if (statusFilter === 'active' && !(t.isActive === 1 || t.isActive === true)) return false;
                    if (statusFilter === 'inactive' && !(t.isActive === 0 || t.isActive === false)) return false;
                    
                    // Search filter - input type detection for TID/NIC/name
                    if (globalFilter && globalFilter.trim() !== '') {
                        const search = globalFilter.trim();
                        // NIC search - exact match (9 digits + char OR 12 digits) - check FIRST
                        if (/^\d{9}[VvXx]$/.test(search) || /^\d{12}$/.test(search)) {
                            return t.nic?.toUpperCase() === search.toUpperCase();
                        }
                        // TID search - exact match (all digits, but not 12-digit NIC)
                        else if (/^\d+$/.test(search)) {
                            return String(t.teacherSignId) === search;
                        }
                        // Name search - substring match (contains letters)
                        else {
                            const searchLower = search.toLowerCase();
                            return (t.firstName?.toLowerCase().includes(searchLower) ||
                                    t.lastName?.toLowerCase().includes(searchLower));
                        }
                    }
                    return true;
                })}
                editMode="cell"
                sortField="teacherSignId"
                sortOrder={1}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                
                header={header}
                emptyMessage="No teachers found"
                className="admin-table"
                stripedRows
                showGridlines={false}
                scrollable
            >
                <Column header="#" body={indexTemplate} frozen alignFrozen="left" style={{ width: '60px', textAlign: 'center', backgroundColor: '#fff' }} />
                <Column 
                    field="empId" 
                    header="Emp_ID" 
                    sortable 
                    style={{ width: '120px' }}
                    body={empIdBodyTemplate}
                    editor={(options) => empIdEditor(options)}
                    onCellEditComplete={onEmpIdEditComplete}
                />
                <Column field="teacherSignId" header="Teacher Sign ID" sortable style={{ width: '120px' }} />
                <Column field="firstName" header="First Name" sortable />
                <Column field="lastName" header="Last Name" sortable />
                <Column field="nic" header="NIC" sortable body={(rowData) => rowData.nic?.toUpperCase() || '-'} />
                <Column field="teacherType" header="Type" sortable />
                <Column field="email" header="Email" />
                <Column field="phone" header="WhatsApp No" style={{ width: '140px' }} />
                <Column field="totalPunches" header="Total Punchs" sortable style={{ width: '120px' }} />
                <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateTemplate} sortable style={{ width: '140px' }} />
                <Column header="Status / Actions" body={statusActionsTemplate} style={{ width: '260px' }} />
            </DataTable>
            </>
            )}

            <Dialog
                visible={showDialog}
                onHide={() => setShowDialog(false)}
                header={editMode ? 'Edit Teacher' : 'Add New Teacher'}
                footer={dialogFooter}
                style={{ width: '600px' }}
                className="admin-dialog"
                modal
            >
                {!editMode && (
                    <button
                        onClick={() => setShowHelp(true)}
                        title="Help"
                        style={{
                            position: 'absolute', top: '14px', right: '52px',
                            width: '32px', height: '32px', borderRadius: '50%',
                            border: 'none', backgroundColor: '#3b82f6', color: 'white',
                            fontSize: '1rem', fontWeight: 700, cursor: 'pointer', zIndex: 1000,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(59,130,246,0.4)'
                        }}
                    >?</button>
                )}
                <div className="form-grid">
                    {!editMode && (
                        <div style={{ display: 'flex', gap: '1rem', gridColumn: '1 / -1' }}>
                            <div className="form-field" style={{ flex: 1 }}>
                                <label>Enrolment ID (emp_ID) *</label>
                                <InputText
                                    value={formData.empId || ''}
                                    onChange={(e) => {
                                        setFormData({ ...formData, empId: e.target.value });
                                        clearTimeout(debounceTimer.current);
                                        debounceTimer.current = setTimeout(() => {
                                            validateEmpId(e.target.value);
                                        }, 500);
                                    }}
                                    placeholder="Enter machine emp_id"
                                    style={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center' }}
                                />
                                {empIdValidation.message && (
                                    <small style={{ color: empIdValidation.valid ? '#2e7d32' : '#c62828' }}>
                                        {empIdValidation.message}
                                    </small>
                                )}
                                <small style={{ color: '#666' }}>Must match fingerprint machine emp_id</small>
                            </div>
                            <div className="form-field" style={{ flex: 1 }}>
                                <label>Sign ID (TID) *</label>
                                <InputText
                                    value={formData.tid || ''}
                                    onChange={(e) => setFormData({ ...formData, tid: e.target.value })}
                                    placeholder="Sign ID"
                                    style={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center' }}
                                />
                                <small
                                    style={{ color: '#b45309', fontWeight: 600, fontSize: '0.85rem', cursor: 'help', display: 'inline-block', fontFamily: 'inherit' }}
                                    onMouseEnter={(e) => { e.target.style.fontFamily = "'Abhaya Libre', 'Noto Sans Sinhala', sans-serif"; e.target.innerText = '⚠ පාසල විසින් ලබා දුන් සංකේත අංකය (Sign ID) නිවැරදිව ඇතුළත් කරන්න'; }}
                                    onMouseLeave={(e) => { e.target.style.fontFamily = 'inherit'; e.target.innerText = '⚠ Carefully enter the Sign ID provided by the school'; }}
                                >
                                    ⚠ Carefully enter the Sign ID provided by the school
                                </small>
                                <small style={{ color: '#666' }}>Suggested next ID — editable if needed</small>
                            </div>
                        </div>
                    )}
                    <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                        <label>NIC Number *</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <InputText
                                value={formData.nic}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setFormData({ ...formData, nic: value });
                                    if (!value || value.length < 5) {
                                        setExistingProfile(null);
                                        setNicLookupStatus({ type: 'idle', message: '' });
                                    }
                                    if (!editMode && value.length >= 5) {
                                        clearTimeout(debounceTimer.current);
                                        debounceTimer.current = setTimeout(() => {
                                            lookupNic(value);
                                        }, 500);
                                    }
                                }}
                                placeholder="Enter NIC number first"
                                style={{ flex: 1 }}
                                disabled={editMode}
                            />
                            {nicLookupLoading && <i className="pi pi-spin pi-spinner" />}
                            {!editMode && (
                                <Button
                                    icon="pi pi-search"
                                    className="p-button-outlined"
                                    onClick={() => lookupNic(formData.nic)}
                                    disabled={!formData.nic || nicLookupLoading}
                                    tooltip="Search NIC"
                                />
                            )}
                        </div>
                        {formData.enrollmentDate && (
                            <small style={{ marginTop: '8px', display: 'block', color: '#2e7d32' }}>
                                Enrolment Date: {new Date(formData.enrollmentDate).toLocaleDateString()}
                            </small>
                        )}
                        {nicLookupStatus.message && !editMode && (
                            <div
                                style={{
                                    marginTop: '8px',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    backgroundColor:
                                        nicLookupStatus.type === 'exists' ? '#e8f5e9' :
                                        nicLookupStatus.type === 'error' ? '#ffebee' :
                                        nicLookupStatus.type === 'loading' ? '#e3f2fd' :
                                        '#fff8e1',
                                    color: nicLookupStatus.type === 'error' ? '#c62828' : '#1f2937'
                                }}
                            >
                                {nicLookupStatus.message}
                            </div>
                        )}
                        {existingProfile && !editMode && (
                            <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '0.9rem' }}>
                                <i className="pi pi-check-circle" style={{ color: '#4caf50', marginRight: '8px' }}></i>
                                <strong>Existing profile found:</strong> {existingProfile.first_name} {existingProfile.last_name} 
                                <span style={{ marginLeft: '10px', color: '#666' }}>
                                    (Currently at: {existingProfile.organizationName || 'Unknown'})
                                </span>
                                <div style={{ marginTop: '5px', color: '#2e7d32' }}>
                                    Will be linked to Ananda College when saved.
                                </div>
                            </div>
                        )}
                        {/* Draft backfill progress bar */}
                        {draftTeacherId && draftBackfillStatus && !editMode && (
                            <div style={{
                                marginTop: '10px', padding: '12px',
                                backgroundColor: draftBackfillStatus === 'completed' ? '#e8f5e9' :
                                                 draftBackfillStatus === 'failed' ? '#ffebee' : '#fff3e0',
                                borderRadius: '6px', border: '1px solid ' + (
                                    draftBackfillStatus === 'completed' ? '#4caf50' :
                                    draftBackfillStatus === 'failed' ? '#ef5350' : '#ff9800'
                                )
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <i className={`pi ${draftBackfillStatus === 'completed' ? 'pi-check-circle' : draftBackfillStatus === 'failed' ? 'pi-times-circle' : 'pi-spin pi-spinner'}`}
                                       style={{ color: draftBackfillStatus === 'completed' ? '#4caf50' : draftBackfillStatus === 'failed' ? '#ef5350' : '#ff9800' }} />
                                    <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1f2937' }}>
                                        {draftBackfillStatus === 'completed' ? 'Backfill complete!' :
                                         draftBackfillStatus === 'failed' ? 'Backfill failed' :
                                         'Backfilling attendance from WDMS...'}
                                    </span>
                                </div>
                                {draftBackfillStatus === 'running' && (
                                    <>
                                        <div style={{ width: '100%', height: '20px', backgroundColor: '#e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                                            <div style={{
                                                width: `${draftBackfillPct}%`, height: '100%',
                                                background: 'linear-gradient(90deg, #f59e0b, #f97316)',
                                                borderRadius: '10px', transition: 'width 0.5s ease',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'white' }}>
                                                    {draftBackfillInfo.total > 0 ? `${draftBackfillInfo.processed}/${draftBackfillInfo.total}` : '...'}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>
                                            {draftBackfillInfo.total > 0 ? `${draftBackfillPct}% — You can continue entering details below` : 'Fetching punch records from WDMS...'}
                                        </div>
                                    </>
                                )}
                                {draftBackfillStatus === 'completed' && (
                                    <div style={{ fontSize: '0.8rem', color: '#2e7d32' }}>
                                        {draftBackfillInfo.total} punches processed. Enter remaining details and click Save.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="form-field">
                        <label>First Name *</label>
                        <InputText
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            placeholder="Enter first name"
                        />
                    </div>
                    <div className="form-field">
                        <label>Last Name *</label>
                        <InputText
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            placeholder="Enter last name"
                        />
                    </div>
                    <div className="form-field">
                        <label>Teacher Type *</label>
                        <Dropdown
                            value={formData.teacherTypeId}
                            options={teacherTypes}
                            onChange={(e) => setFormData({ ...formData, teacherTypeId: e.value })}
                            placeholder="Select type"
                            optionLabel="label"
                            optionValue="value"
                        />
                    </div>
                    <div className="form-field">
                        <label>Email *</label>
                        <InputText
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="Enter email"
                            type="email"
                        />
                    </div>
                    <div className="form-field">
                        <label>WhatsApp No *</label>
                        <InputText
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="Enter WhatsApp No"
                        />
                    </div>
                </div>
                {lastSavedTeacher && !editMode && (
                    <div style={{ marginTop: '1rem', padding: '12px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                        <i className="pi pi-check-circle" style={{ color: '#4caf50', fontSize: '1.5rem', marginRight: '8px' }}></i>
                        <strong>Teacher saved!</strong>
                        <span style={{ marginLeft: '10px' }}>Emp ID: <strong>{lastSavedTeacher.empId}</strong> | Sign ID: <strong>{lastSavedTeacher.tid}</strong></span>
                        <div style={{ marginTop: '10px' }}>
                            <Button label="Print Registration" icon="pi pi-print" className="p-button-info" onClick={printRegistration} />
                            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} style={{ marginLeft: '8px' }} />
                        </div>
                    </div>
                )}
            </Dialog>

            {showHelp && <AddTeacherHelp onClose={() => setShowHelp(false)} />}

            {/* TID Conflict Dialog */}
            <Dialog
                visible={showConflictDialog}
                onHide={() => setShowConflictDialog(false)}
                header="TID Conflict Detected"
                footer={
                    <div>
                        {tidConflict?.canReassign && (
                            <Button
                                label="Reassign TID"
                                icon="pi pi-refresh"
                                className="p-button-success"
                                onClick={handleReassignTid}
                            />
                        )}
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setShowConflictDialog(false)}
                        />
                    </div>
                }
                style={{ width: '500px' }}
            >
                {tidConflict && (
                    <div>
                        <Message
                            severity="warn"
                            style={{ marginBottom: '15px' }}
                            content={`TID ${tidConflict.tid} is already assigned to another teacher.`}
                        />
                        
                        <div style={{ 
                            padding: '15px', 
                            background: '#f5f5f5', 
                            borderRadius: '8px',
                            marginBottom: '15px'
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#1e3a5f' }}>
                                Current TID Holder
                            </h4>
                            <div style={{ fontSize: '0.95rem', lineHeight: '1.8' }}>
                                <div><strong>Name:</strong> {tidConflict.firstName} {tidConflict.lastName}</div>
                                <div><strong>NIC:</strong> {tidConflict.nic}</div>
                                <div><strong>School:</strong> {tidConflict.schoolName || 'Unknown'}</div>
                                <div>
                                    <strong>Status:</strong> 
                                    <span style={{ 
                                        color: tidConflict.isActive ? '#2e7d32' : '#c62828',
                                        marginLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        {tidConflict.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {tidConflict.canReassign ? (
                            <Message
                                severity="info"
                                content={
                                    <div>
                                        <strong>Reassignment Allowed</strong>
                                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem' }}>
                                            {tidConflict.isActive 
                                                ? 'Teacher is from a different school. You can reassign this TID to the new teacher.'
                                                : 'Teacher is inactive. You can reassign this TID to the new teacher.'}
                                        </p>
                                    </div>
                                }
                            />
                        ) : (
                            <Message
                                severity="error"
                                content={
                                    <div>
                                        <strong>Reassignment Not Allowed</strong>
                                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem' }}>
                                            TID is assigned to an active teacher in the same school. Please use a different TID.
                                        </p>
                                    </div>
                                }
                            />
                        )}
                    </div>
                )}
            </Dialog>

            {/* PIN Dialog for emp_id changes */}
            <Dialog
                visible={showPinDialog}
                onHide={() => {
                    setShowPinDialog(false);
                    setPinInput('');
                }}
                header="Confirm emp_id Change"
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setShowPinDialog(false);
                                setPinInput('');
                            }}
                        />
                        <Button
                            label="Confirm"
                            icon="pi pi-check"
                            onClick={handlePinSubmit}
                        />
                    </div>
                }
                style={{ width: '400px' }}
            >
                <div>
                    <Message
                        severity="warn"
                        style={{ marginBottom: '15px' }}
                        content="Changing emp_id requires authorization. Enter PIN to confirm."
                    />
                    <div className="form-field">
                        <label>PIN *</label>
                        <InputText
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value)}
                            type="password"
                            placeholder="Enter PIN"
                            style={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center', letterSpacing: '4px' }}
                            maxLength={4}
                            autoFocus
                        />
                    </div>
                    {empIdInlineValidation.teacherId === editingEmpId.teacherId && empIdInlineValidation.message && (
                        <small style={{ 
                            color: empIdInlineValidation.valid ? '#2e7d32' : '#c62828',
                            display: 'block',
                            marginTop: '8px'
                        }}>
                            {empIdInlineValidation.message}
                        </small>
                    )}
                </div>
            </Dialog>

            <Dialog
                visible={showConfirmDialog}
                onHide={handleConfirmNo}
                header="Confirm emp_id Assignment"
                footer={
                    <div>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={handleConfirmNo}
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            className="p-button-danger"
                            onClick={handleConfirmYes}
                        />
                    </div>
                }
                style={{ width: '500px' }}
            >
                <div>
                    {empIdHolderInfo?.hasHolder ? (
                        <div>
                            <Message
                                severity="warn"
                                style={{ marginBottom: '15px' }}
                                content="This emp_id is currently assigned to another teacher."
                            />
                            <p style={{ marginBottom: '10px' }}>
                                <strong>Current holder:</strong> {empIdHolderInfo.holder.firstName} {empIdHolderInfo.holder.lastName}
                            </p>
                            <p style={{ marginBottom: '10px' }}>
                                <strong>Punch count:</strong> {empIdHolderInfo.holder.punchCount}
                            </p>
                            <p style={{ marginBottom: '10px' }}>
                                <strong>Registered on:</strong> {new Date(empIdHolderInfo.holder.registrationDate).toLocaleDateString()}
                            </p>
                            <hr style={{ margin: '15px 0' }} />
                            <p style={{ marginBottom: '10px' }}>
                                You are going to assign emp_id <strong>{editingEmpId.value}</strong> to <strong>{editingEmpId.teacherName}</strong>.
                            </p>
                            <p style={{ color: '#c62828', fontWeight: 'bold' }}>
                                Are you sure?
                            </p>
                        </div>
                    ) : (
                        <div>
                            <Message
                                severity="info"
                                style={{ marginBottom: '15px' }}
                                content="This emp_id is not currently assigned to any teacher."
                            />
                            <p style={{ marginBottom: '10px' }}>
                                You are going to assign emp_id <strong>{editingEmpId.value}</strong> to <strong>{editingEmpId.teacherName}</strong>.
                            </p>
                            <p>
                                Are you sure?
                            </p>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default TeacherManagement;
